/**
 * Whitelabel Configuration Service
 * 
 * This service detects whether the app is running in whitelabel mode
 * and provides configuration options to hide FracTEL branding.
 * 
 * Whitelabel mode is activated when:
 * 1. The app is loaded from whitelabel-fractelfone.fractel.net
 * 2. The WHITELABEL_TEST environment variable is set to 'true'
 * 3. The URL contains ?whitelabel=true query parameter (for testing)
 */

const WHITELABEL_HOSTNAME = 'whitelabel-fractelfone.fractel.com'

/**
 * Check if the current hostname matches the whitelabel URL
 */
function isWhitelabelHostname() {
  if (typeof window !== 'undefined') {
    return window.location.hostname === WHITELABEL_HOSTNAME
  }
  return false
}

/**
 * Check if whitelabel mode is enabled via URL query parameter (for testing)
 */
function isWhitelabelQueryParam() {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('whitelabel') === 'true'
  }
  return false
}

/**
 * Check if whitelabel mode is enabled via environment variable
 */
function isWhitelabelEnv() {
  return process.env.VUE_APP_WHITELABEL === 'true'
}

/**
 * Main whitelabel detection function
 * Returns true if the app should run in whitelabel mode
 * Default is false (FracTELfone branding shown)
 */
function isWhiteLabel() {
  return isWhitelabelHostname() || isWhitelabelQueryParam() || isWhitelabelEnv()
}

/**
 * Get the app name based on whitelabel mode
 */
function getAppName() {
  return isWhiteLabel() ? 'Softphone' : 'FracTELfone'
}

/**
 * Get the company name based on whitelabel mode
 */
function getCompanyName() {
  return isWhiteLabel() ? '' : 'FracTEL, LLC'
}

/**
 * Check if logos should be shown
 */
function showLogo() {
  return !isWhiteLabel()
}

/**
 * Check if FracTEL branding should be shown
 */
function showBranding() {
  return !isWhiteLabel()
}

/**
 * Get the User Guide URL based on whitelabel mode
 */
function getUserGuideUrl() {
  return isWhiteLabel() ? '' : 'http://fractel.com/wp-content/uploads/2021/01/FracTELfone-User-Guide.pdf'
}

/**
 * Check if the user guide link should be shown
 */
function showUserGuide() {
  return !isWhiteLabel() && getUserGuideUrl() !== ''
}

/**
 * Whitelabel configuration object
 * Import this in Vue components to access whitelabel settings
 */
const WhitelabelConfig = {
  isWhiteLabel,
  isWhitelabelHostname,
  isWhitelabelQueryParam,
  isWhitelabelEnv,
  getAppName,
  getCompanyName,
  showLogo,
  showBranding,
  getUserGuideUrl,
  showUserGuide,
  WHITELABEL_HOSTNAME
}

export default WhitelabelConfig

export {
  isWhiteLabel,
  isWhitelabelHostname,
  isWhitelabelQueryParam,
  isWhitelabelEnv,
  getAppName,
  getCompanyName,
  showLogo,
  showBranding,
  getUserGuideUrl,
  showUserGuide,
  WHITELABEL_HOSTNAME
}
